<template>
	<div class="training-quiz">
    <div class="container">
      <h2 class="training-main-name quiz-title" v-html="quiz.name"></h2>

      <ActionQuizNavigationBar
          v-if="showProgressBar"
          :countOfBlocks="quizLength"
          :done="trainingAction.done"
          :agreeSlide="false"
          :correctIndexes="correctIndexes"
          :showCorrectAnswers="showCorrectAnswers"
      />

      <div v-if="!startQuiz" class="quiz-info">
        <div class="quiz-stats">
          <div>Number of questions: {{ quizLength }}</div>
          <div>Required score: 100%</div>
          <div>Number of attempts: {{ attemptsCount }}</div>
          <div>Current score: {{ quizScore }}%</div>
        </div>

        <div class="submit-button" v-if="!isComplete">
          <f7-button @click="startQuizFunc">
            {{ buttonName }} Assessment
          </f7-button>
        </div>
      </div>
    </div>

    <div class="swiper-self-wrapper" v-if="startQuiz">
      <swiper
          class="swiper"
          v-bind="swiperParameters"
          @swiper="onSwiperInit"
          @resize="restoreSlide"
          @update="restoreSlide"
      >
        <swiper-slide
            v-for="(question, questionIdx) in questionsToShow"
            :key="question.id">
          <div class="swiper-slide-wrapper">
            <div class="slider-body card-shadow1">
              <div class="quiz-section-header" v-if="sectionHeaders[question.id]" v-html="sectionHeaders[question.id]"></div>
              <div class="procedure-title">
                <span class="question-counter">{{ questionIdx + 1 }} / {{ quizLength }}. </span>
                {{ question.question }}
              </div>
              <ol class="custom-list" v-if="question.type === 1">
                <li>
                  <div class="submit-button answer-option">
                    <f7-button @click="handleBoolAnswer(question.id, true)">
                      <div class="button-letter-wrapper"><span class="button-text"></span></div>
                      <div>Yes</div>
                    </f7-button>
                  </div>
                </li>
                <li>
                  <div class="submit-button answer-option">
                    <f7-button @click="handleBoolAnswer(question.id, false)">
                      <div class="button-letter-wrapper"><span class="button-text"></span></div>
                      <div>No</div>
                    </f7-button>
                  </div>
                </li>
              </ol>

              <!-- type 4: free text -->
              <div class="text-answer" v-else-if="question.type === 4">
                <textarea
                    class="text-answer-input"
                    rows="4"
                    v-model="textAnswers[question.id]"
                    placeholder="Your answer"
                ></textarea>
                <div class="submit-button answer-option">
                  <f7-button @click="handleTextAnswer(question.id, textAnswers[question.id])">
                    Submit
                  </f7-button>
                </div>
              </div>

              <ol class="custom-list" v-else>
                <li v-for="(option, optionIdx) in question.question_items" :key="optionIdx">
                  <div class="submit-button answer-option">
                    <f7-button @click="handleAnswer(question.id, optionIdx)">
                      <div class="button-letter-wrapper"><span class="button-text"></span></div>
                      <div>{{ option }}</div>
                    </f7-button>
                  </div>
                </li>
              </ol>
            </div>
          </div>
        </swiper-slide>
      </swiper>
    </div>
  </div>
</template>

<script>
  import ActionQuizNavigationBar from "@/components/ActionQuizNavigationBar.vue";
  import { nextTick } from "vue";
  import { Swiper, SwiperSlide } from "swiper/vue";
  import "swiper/css";

  const REQUIRED_SCORE = 100;

  export default {
    components: {
      Swiper,
      SwiperSlide,
      ActionQuizNavigationBar,
    },

    props: {
      quiz: { type: Object, required: true },
      trainingAction: { type: Object, required: true },
      sectionHeaders: { type: Object, default: () => ({}) },
    },

    emits: ['submit', 'slideChanged', 'changeSlideFromQuiz'],

    data() {
      return {
        index: 0,
        swiper: null,
        questions: [],
        questionsToShow: [],
        attemptAnswers: {},
        textAnswers: {},
        correctIndexes: [],
        showProgressBar: true,
        startQuiz: false,
        showCorrectAnswers: false,
        swiperParameters: {
          slidesPerView: 1,
          autoHeight: true,
          allowTouchMove: false,
        },
      };
    },

    computed: {
      quizScore() {
        if (this.trainingAction.done) return REQUIRED_SCORE;
        if (!this.questions.length) return 0;
        const correct = this.questions.filter(this.isCorrect).length;
        return Math.round((correct / this.questions.length) * 100);
      },

      quizLength() {
        return this.startQuiz ? this.questionsToShow.length : this.questions.length;
      },

      answerableQuestions() {
        return this.questions.filter(q => q.type !== 9);
      },

      answeredCount() {
        return this.questionsToShow.filter(q => this.attemptAnswers[q.id]).length;
      },

      attemptsCount() {
        if (!this.questions.length) return 0;
        return Math.max(...this.questions.map(q => q.answers?.length ?? 0));
      },

      isComplete() {
        return this.quizScore === REQUIRED_SCORE;
      },

      buttonName() {
        return this.questions.length !== this.questionsToShow.length || this.index > 0
          ? 'Continue'
          : 'Start';
      },
    },

    mounted() {
      this.questions = [...this.quiz.questions].sort((a, b) => a.id - b.id);
      this.createQuestions();
      if (this.attemptsCount > 0) {
        this.correctIndexes = this.questions
          .map((q, i) => this.isCorrect(q) ? i : null)
          .filter(i => i !== null);
        this.showCorrectAnswers = true;
      }

      document.addEventListener("visibilitychange", this.restoreSlide);
      window.addEventListener("focus", this.restoreSlide);
    },

    unmounted() {
      document.removeEventListener("visibilitychange", this.restoreSlide);
      window.removeEventListener("focus", this.restoreSlide);
    },

    methods: {
      onSwiperInit(swiper) {
        this.swiper = swiper;
      },

      restoreSlide() {
        if (!this.startQuiz || document.hidden) return;
        if (!this.swiper || this.swiper.destroyed) return;

        const target = Math.min(this.index, this.questionsToShow.length - 1);
        if (target < 0 || this.swiper.activeIndex === target) return;

        this.swiper.slideTo(target, 0, false, true);
        this.$emit('slideChanged', target);
      },

      async startQuizFunc() {
        await this.resetQuiz();
        this.startQuiz = true;
        this.$emit('slideChanged', this.index);
      },

      handleAnswer(questionId, answer) {
        const question = this.questionToAnswer(questionId);
        if (!question) return;

        const correctIndexes = String(question.dataText ?? '').split(',').map(s => s.trim());
        this.recordAnswer(question, { dataText: answer, result: correctIndexes.includes(String(answer)) });
      },

      handleBoolAnswer(questionId, value) {
        const question = this.questionToAnswer(questionId);
        if (!question) return;

        this.recordAnswer(question, { dataBool: value, result: question.dataBool == value });
      },

      handleTextAnswer(questionId, value) {
        if (!value || !String(value).trim()) return;

        const question = this.questionToAnswer(questionId);
        if (!question) return;

        const correct = String(question.dataText ?? '').trim() === String(value).trim();
        this.recordAnswer(question, { dataText: value, result: correct });
      },

      questionToAnswer(questionId) {
        if (this.attemptAnswers[questionId]) {
          this.restoreSlide();
          return null;
        }

        return this.questionsToShow.find(q => q.id === questionId) ?? null;
      },

      recordAnswer(question, answer) {
        this.attemptAnswers[question.id] = answer;
        question.answers.push(answer);

        this.advance();
      },

      advance() {
        this.index = this.answeredCount;

        if (this.index < this.questionsToShow.length) {
          if (this.questionsToShow.length > 1) this.emitNavigation(this.index);
          return;
        }

        this.submitAnswers();
      },

      collectAnswers() {
        const serialize = (questionId, answer) => {
          const payload = { questions: questionId };
          if (answer.dataText !== undefined) payload.dataText = answer.dataText;
          if (answer.dataBool !== undefined) payload.dataBool = answer.dataBool;
          return payload;
        };

        return this.answerableQuestions
          .map(question => {
            const answer = this.attemptAnswers[question.id] ?? question.answers[question.answers.length - 1];

            return answer ? serialize(question.id, answer) : null;
          })
          .filter(Boolean);
      },

      async submitAnswers() {
        const answers = this.collectAnswers();

        if (answers.length < this.answerableQuestions.length) {
          this.discardAttempt();
          this.startQuiz = false;
          await this.resetQuiz();
          return;
        }

        this.showResults();

        await this.$store.dispatch('app/saveAnswers', {
          action: this.trainingAction.id,
          answers,
        }).catch(() => null);

        await this.$store.dispatch('trainings/getTrainings');
        this.questions = [...this.quiz.questions].sort((a, b) => a.id - b.id);
        this.$emit('submit');
      },

      discardAttempt() {
        for (const question of this.questionsToShow) {
          const answer = this.attemptAnswers[question.id];
          if (!answer) continue;

          const index = question.answers.indexOf(answer);
          if (index !== -1) question.answers.splice(index, 1);
        }
      },

      showResults() {
        this.correctIndexes = this.questions
          .map((q, i) => this.isCorrect(q) ? i : null)
          .filter(i => i !== null);
        this.showCorrectAnswers = true;
        this.startQuiz = false;

        if (this.isComplete) {
          this.trainingAction.done = true;
        }
      },

      createQuestions() {
        this.questionsToShow = this.questions.filter(q => !this.isCorrect(q));
        if (!this.questionsToShow.length) this.showProgressBar = false;
      },

      async resetQuiz() {
        this.createQuestions();
        this.index = 0;
        this.attemptAnswers = {};
        this.correctIndexes = [];
        this.showCorrectAnswers = false;
        await this.remountProgressBar();
      },

      async remountProgressBar() {
        this.showProgressBar = false;
        await nextTick();
        this.showProgressBar = true;
      },

      emitNavigation(index) {
        this.$emit('changeSlideFromQuiz', index);
        this.$emit('slideChanged', index);
      },

      isCorrect(question) {
        return question.answers.some(a => a.result);
      },
    },
  };
</script>

<style scoped>
.quiz-title {
  text-align: center;
}

.quiz-info {
  margin-top: 24px;
}

.answer-option {
  width: 100%;
}

.button-letter-wrapper {
  margin-right: 0.5rem;
}

.question-counter {
  white-space: nowrap;
}

.quiz-section-header {
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--card-border-color);
  font-weight: 700;
  font-size: 18px;
  line-height: 130%;
}

.training-quiz .submit-button a {
  margin: 0;
}

.text-answer-input {
  width: 100%;
  margin: 12px 0;
  padding: 10px;
  border: 1px solid var(--card-border-color);
  border-radius: 8px;
  background: transparent;
  color: inherit;
  font-family: inherit;
  font-size: 16px;
  resize: vertical;
}
</style>
